import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

import DnaBackground from '../components/DnaBackground';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import TypeAvatar from '../components/TypeAvatar';
import Seo from '../components/Seo';
import { getAllFounderTypes, getTypeProfilePath } from '../data/founderTypes';
import { formatFounderTitle } from '../utils/textUtils';

const Page = styled.div`
  min-height: 100vh;
  padding: 2.5rem 1.25rem 3rem;
  position: relative;
  z-index: 1;
`;

const Container = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

const Hero = styled.div`
  text-align: center;
  margin: 2rem 0 2.5rem;
`;

const Title = styled.h1`
  font-family: var(--font-display);
  font-size: clamp(2rem, 5vw, 2.75rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  margin-bottom: 0.75rem;
  color: var(--text-primary);
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  font-size: 1.1rem;
  line-height: 1.6;
  max-width: 560px;
  margin: 0 auto 1.5rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.25rem;
`;

const TypeCard = styled(motion(Link))`
  display: block;
  padding: 1.5rem;
  border-radius: 16px;
  background: rgba(18, 18, 28, 0.75);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  transition: border-color 0.2s ease, transform 0.2s ease;
  text-align: center;

  &:hover {
    border-color: ${(props) => `${props.$color}66`};
    transform: translateY(-4px);
  }
`;

const TypeName = styled.h2`
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0.75rem 0 0.35rem;
`;

const TypeTag = styled.p`
  font-size: 0.95rem;
  color: var(--text-secondary);
  line-height: 1.5;
  margin: 0;
`;

const Code = styled.span`
  display: inline-block;
  margin-top: 0.5rem;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.15em;
  color: ${(props) => props.$color};
`;

const TypesIndex = () => {
  const types = getAllFounderTypes();

  return (
    <Page>
      <Seo
        title="Founder types"
        description="Explore the six founder operating modes: Visionary, Executor, Innovator, Analyst, Strategist, and Relationship Builder."
        path="/types"
      />
      <DnaBackground />
      <Container>
        <SiteHeader showCta />
        <Hero>
          <Title>The six founder operating modes</Title>
          <Subtitle>
            Every founder leads differently. Explore each mode, then take the free assessment to discover your blend.
          </Subtitle>
        </Hero>
        <Grid>
          {types.map((type, index) => (
            <TypeCard
              key={type.id}
              to={getTypeProfilePath(type)}
              $color={type.color}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.06 }}
            >
              <TypeAvatar type={type} size={88} animate={false} />
              <TypeName>{formatFounderTitle(type.title)}</TypeName>
              <Code $color={type.color}>{type.code}</Code>
              <TypeTag>{type.tagline}</TypeTag>
            </TypeCard>
          ))}
        </Grid>
        <SiteFooter />
      </Container>
    </Page>
  );
};

export default TypesIndex;
